import React, { useState } from 'react'
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Popconfirm, Space, Tag, Empty, message } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useI18n } from '@/shared/i18n'
import { usePolicies, useCreatePolicy, useUpdatePolicy, useDeletePolicy, useTogglePolicy } from '@/shared/api/policyApi'
import type { Policy, PolicyCreateRequest, PolicyUpdateRequest } from '@/shared/api/types'

const PolicyManagement: React.FC = () => {
  const { t } = useI18n('modules')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null)
  const [form] = Form.useForm()

  const { data, isLoading } = usePolicies()
  const createMutation = useCreatePolicy()
  const updateMutation = useUpdatePolicy()
  const deleteMutation = useDeletePolicy()
  const toggleMutation = useTogglePolicy()

  const policies = data?.policies ?? []

  const openCreateModal = () => {
    setEditingPolicy(null)
    form.resetFields()
    form.setFieldsValue({ is_active: true, priority: 0 })
    setModalOpen(true)
  }

  const openEditModal = (policy: Policy) => {
    setEditingPolicy(policy)
    form.setFieldsValue(policy)
    setModalOpen(true)
  }

  const handleSubmit = async () => {
    const values = await form.validateFields()
    if (editingPolicy) {
      const updateData: PolicyUpdateRequest = values
      updateMutation.mutate({ id: editingPolicy.id, data: updateData }, {
        onSuccess: () => {
          message.success(t('agentConfig.policies.saveSuccess'))
          setModalOpen(false)
        },
      })
    } else {
      const createData: PolicyCreateRequest = values
      createMutation.mutate(createData, {
        onSuccess: () => {
          message.success(t('agentConfig.policies.saveSuccess'))
          setModalOpen(false)
        },
      })
    }
  }

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        message.success(t('agentConfig.policies.deleteSuccess'))
      },
    })
  }

  const handleToggle = (id: number) => {
    toggleMutation.mutate(id)
  }

  const columns: ColumnsType<Policy> = [
    {
      title: t('agentConfig.policies.name'),
      dataIndex: 'name',
      width: 160,
    },
    {
      title: t('agentConfig.policies.description'),
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: t('agentConfig.policies.priority'),
      dataIndex: 'priority',
      width: 80,
      align: 'center',
    },
    {
      title: t('agentConfig.policies.isActive'),
      dataIndex: 'is_active',
      width: 80,
      align: 'center',
      render: (active: boolean, record: Policy) => (
        <Switch
          checked={active}
          size="small"
          onChange={() => handleToggle(record.id)}
          loading={toggleMutation.isPending}
        />
      ),
    },
    {
      title: t('agentConfig.policies.updated'),
      dataIndex: 'updated_at',
      width: 160,
      render: (val: string) => new Date(val).toLocaleString(),
    },
    {
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_: unknown, record: Policy) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          />
          <Popconfirm
            title={t('agentConfig.policies.deleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          {t('agentConfig.policies.add')}
        </Button>
      </div>

      <Table<Policy>
        columns={columns}
        dataSource={policies}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              description={
                <span>
                  <div>{t('agentConfig.policies.empty')}</div>
                  <div style={{ color: 'var(--text-color-secondary)', fontSize: 12, marginTop: 4 }}>
                    {t('agentConfig.policies.emptyDescription')}
                  </div>
                </span>
              }
            />
          ),
        }}
      />

      <Modal
        title={editingPolicy ? t('agentConfig.policies.edit') : t('agentConfig.policies.add')}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label={t('agentConfig.policies.name')}
            name="name"
            rules={[{ required: true, max: 100 }]}
          >
            <Input placeholder={t('agentConfig.policies.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('agentConfig.policies.description')}
            name="description"
            rules={[{ required: true, max: 500 }]}
            help={t('agentConfig.policies.descriptionHelp')}
          >
            <Input.TextArea
              placeholder={t('agentConfig.policies.descriptionPlaceholder')}
              rows={2}
            />
          </Form.Item>

          <Form.Item
            label={t('agentConfig.policies.content')}
            name="content"
            rules={[{ required: true }]}
            help={t('agentConfig.policies.contentHelp')}
          >
            <Input.TextArea
              placeholder={t('agentConfig.policies.contentPlaceholder')}
              rows={8}
            />
          </Form.Item>

          <Space size="large">
            <Form.Item label={t('agentConfig.policies.priority')} name="priority">
              <InputNumber min={0} max={999} />
            </Form.Item>

            <Form.Item
              label={t('agentConfig.policies.isActive')}
              name="is_active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  )
}

export default PolicyManagement
